import { kindOf, type DraftDto, type PublicationKind, type RenderDraftStateDto, type RenderVariantDto } from '@app/shared';
import type { Dictionary } from '../i18n';
import { creditsCount } from './format';

/*
 * «Формат и текст» (spec 4): the look a screen starts on, the format chips, the
 * example, the price line and the main button — pure, the page only draws them.
 */

/** What the person sees on «Формат и текст»: the format, its style, the carousel kind. */
export interface Look {
  kind: PublicationKind;
  style: string | null;
  animated: boolean;
}

type DraftShape = Pick<DraftDto, 'format' | 'type' | 'status' | 'version'>;

/** The format of the draft as a chip: «Сцены», «Схема», «Подборка», «Карусель». */
export function draftKind(draft: Pick<DraftDto, 'format' | 'type'>): PublicationKind {
  return kindOf(draft.format, draft.type);
}

export function variantOf(state: RenderDraftStateDto, kind: PublicationKind): RenderVariantDto | null {
  return state.variants.find((v) => v.kind === kind) ?? null;
}

const hasStyle = (v: RenderVariantDto | null, style: string | null): style is string =>
  style !== null && v !== null && v.styles.some((s) => s.id === style);

/**
 * The draft's format (a new draft carries the card's advice); the stored style
 * while the format has it, else the default of the type and the niche; a carousel
 * animated unless chosen, or built, static (a free rebuild stays free).
 */
export function initialLook(state: RenderDraftStateDto, draft: Pick<DraftDto, 'format' | 'type'>): Look {
  const kind = draftKind(draft);
  const v = variantOf(state, kind);
  return {
    kind,
    style: hasStyle(v, state.style) ? state.style : (v?.defaultStyle ?? null),
    animated: state.animated ?? state.lastAnimated ?? true,
  };
}

/** Another format keeps a style it also has (a carousel has the styles of «Сцены»), else its default. */
export function lookAfterKind(state: RenderDraftStateDto, look: Look, kind: PublicationKind): Look {
  const v = variantOf(state, kind);
  return { ...look, kind, style: hasStyle(v, look.style) ? look.style : (v?.defaultStyle ?? null) };
}

const hasText = (draft: DraftShape) => draft.version > 0;

/**
 * The format chips (spec 4.2, 4.5): the advised one has the dot. All closed
 * while the text is written or edited; with text another format is a rewrite —
 * closed when none are left, or while a failed rewrite waits for «Попробовать ещё раз».
 */
export function formatChips(
  state: RenderDraftStateDto,
  draft: DraftShape,
): Array<{ kind: PublicationKind; advised: boolean; closed: boolean }> {
  const busy = draft.status === 'writing' || draft.status === 'editing';
  const current = draftKind(draft);
  const noRewrite = hasText(draft) && (state.rewritesLeft === 0 || draft.status === 'failed');
  return state.variants.map((v) => ({
    kind: v.kind,
    advised: v.kind === state.advisedKind,
    closed: busy || (noRewrite && v.kind !== current),
  }));
}

export type Example = { kind: 'video'; src: string; poster: string | null } | { kind: 'image'; src: string };

/** The one example on the screen (spec 4.1): a reel's demo with its picture as the poster, a carousel's cover. */
export function exampleOf(state: RenderDraftStateDto, look: Look): Example | null {
  const style = variantOf(state, look.kind)?.styles.find((s) => s.id === look.style);
  if (!style) return null;
  if (look.kind !== 'carousel' && style.previewVideoUrl) {
    return { kind: 'video', src: style.previewVideoUrl, poster: style.previewUrl };
  }
  return style.previewUrl ? { kind: 'image', src: style.previewUrl } : null;
}

const styleName = (state: RenderDraftStateDto, look: Look) =>
  variantOf(state, look.kind)?.styles.find((s) => s.id === look.style)?.name ?? look.style ?? '';

/** «Рилс · Подборка · Стекло — пример», «Карусель · Ночь — пример». */
export function exampleCaption(state: RenderDraftStateDto, look: Look, t: Dictionary): string {
  const f = t.formatText;
  const style = styleName(state, look);
  return look.kind === 'carousel' ? f.captionCarousel(style) : f.captionReel(f.formats[look.kind], style);
}

/** The collapsed choice above the text: «Рилс · Подборка · Стекло», «Карусель · Ночь». */
export function lookLine(state: RenderDraftStateDto, look: Look, t: Dictionary): string {
  const f = t.formatText;
  const style = styleName(state, look);
  return look.kind === 'carousel' ? f.lookCarousel(style) : f.lookReel(f.formats[look.kind], style);
}

/** «Рилс «Подборка» — 30 кредитов», «Карусель анимированная — 20 кредитов»: a new paid result of the format. */
export function lookPriceLine(state: RenderDraftStateDto, look: Look, t: Dictionary): string | null {
  const v = variantOf(state, look.kind);
  if (!v) return null;
  const f = t.formatText;
  if (look.kind !== 'carousel') return f.priceReel(f.formats[look.kind], creditsCount(v.price));
  const price = look.animated ? v.price : (v.staticPrice ?? v.price);
  return f.priceCarousel(look.animated, creditsCount(price));
}

/**
 * Replies arrive in any order (a save and a style tap at once): the screen keeps
 * the newest draft — a higher version, or the same version changed later.
 */
export function newerDraft<D extends { version: number; updatedAt: string }>(cached: D | undefined, incoming: D): D {
  if (!cached || incoming.version > cached.version) return incoming;
  if (incoming.version < cached.version) return cached;
  return Date.parse(incoming.updatedAt) >= Date.parse(cached.updatedAt) ? incoming : cached;
}

/**
 * The style to store before the text goes on (spec 4.2): the one on screen, when
 * the draft holds none or another. A style left to the default would follow the
 * person's last build of the type and could change between builds.
 */
export function styleToPin(state: Pick<RenderDraftStateDto, 'style'>, look: Look): string | null {
  return look.style !== null && look.style !== state.style ? look.style : null;
}

export interface MainAction {
  action: 'write' | 'wait' | 'retry' | 'next' | 'build';
  label: string;
  enabled: boolean;
}

/**
 * The one big button (spec 4.3, 4.6): «Написать текст» before the text; it waits
 * while the text is written or edited; «Попробовать ещё раз» after a failure; with
 * text a reel goes on to «Озвучка», a carousel builds at the price of its kind.
 * Form issues or a request in flight close it.
 */
export function mainAction(
  draft: DraftShape,
  look: Look,
  state: RenderDraftStateDto,
  opts: { issues: number; pending: boolean },
  t: Dictionary,
): MainAction {
  const f = t.formatText;
  const free = !opts.pending;
  switch (draft.status) {
    case 'new':
      return { action: 'write', label: f.write, enabled: free };
    case 'writing':
      return { action: 'wait', label: f.writing, enabled: false };
    case 'editing':
      return { action: 'wait', label: f.aiEditing, enabled: false };
    case 'failed':
      return { action: 'retry', label: f.retry, enabled: free };
  }
  const enabled = free && opts.issues === 0;
  if (draft.format === 'reel') return { action: 'next', label: f.next, enabled };
  const quote = look.animated ? state.quote.animated : state.quote.static;
  return { action: 'build', label: quote.free ? f.buildFree : f.build(creditsCount(quote.price)), enabled };
}
